import { UploadCard } from "components/UploadCard";
import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import React, { useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { createId } from "utils/general";

import type { EnergyAnalysisFileSection, FileWithInfoLocal } from "../../model/types";

import { ENERGY_ANALYSIS_SECTIONS } from "./constants";

export const FileUploadStep = () => {
  const { t } = useTranslation();
  const { formData, setFormData } = useContext(SingleBuildingContext);

  const handleFileChange = useCallback(
    (section: EnergyAnalysisFileSection) => (file: File | undefined) => {
      if (!file) return;
      setFormData((prev) => {
        const prevFiles = { ...(prev.files ?? {}) };
        return {
          ...prev,
          files: { ...prevFiles, [section]: [...(prevFiles[section] ?? []), { file, id: createId() }] },
        };
      });
    },
    [setFormData]
  );

  const handleRemoveFile = useCallback(
    (section: EnergyAnalysisFileSection, file: File) => {
      setFormData((prev) => {
        const prevFiles = { ...(prev.files ?? {}) };
        return {
          ...prev,
          files: { ...prevFiles, [section]: prevFiles?.[section]?.filter((f: FileWithInfoLocal) => f.file !== file) },
        };
      });
    },
    [setFormData]
  );

  return (
    <div className="mx-auto w-full max-w-[90rem] rounded-md  bg-lds-surface-bright p-10">
      <h1 className="font-ds-headline-xl">{t("AddBuilding.energyAnalysisStep.title")}</h1>
      <p className="mt-2 text-lds-foreground-muted font-ds-body-regular-md">
        {t("AddBuilding.energyAnalysisStep.subtitle")}
      </p>
      <div className="mt-8 grid grid-cols-2 gap-7">
        {ENERGY_ANALYSIS_SECTIONS.map(({ acceptedFormats, section }) => (
          <UploadCard
            acceptedFormats={acceptedFormats}
            description={t(`AddBuilding.energyAnalysisStep.cards.${section}.description`)}
            files={formData?.files?.[section]?.map((f: FileWithInfoLocal) => f.file) ?? undefined}
            handleFileChange={handleFileChange(section)}
            key={section}
            onRemoveFile={(file: File) => handleRemoveFile(section, file)}
            title={t(`AddBuilding.energyAnalysisStep.cards.${section}.title`)}
          />
        ))}
      </div>
    </div>
  );
};
